// Auth Context - React context for authentication state
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  uid?: string; // alias for compatibility
  name: string;
  displayName?: string; // alias for compatibility
  email: string;
  role: string;
  clan?: string;
  rank?: string;
  trust_score?: number;
  trustScore?: number;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (name: string, email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Use production URL in prod, proxy in dev
const API_BASE = import.meta.env.PROD 
  ? 'https://izwi-api.orbxcode.workers.dev/api/auth' 
  : '/api/auth';

const getStoredToken = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('izwi_token');
};

const getStoredSession = () => {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem('izwi_session');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
  return null;
};

// Normalize user object for compatibility
const normalizeUser = (user: any): User | null => {
  if (!user) return null;
  return {
    ...user,
    uid: user.id,
    displayName: user.name,
    trustScore: user.trust_score,
  };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on mount
    const checkSession = async () => {
      const token = getStoredToken();
      const stored = getStoredSession();
      const authToken = token || stored?.session?.token;
      
      if (authToken) {
        try {
          const res = await fetch(`${API_BASE}/get-session`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          const data = await res.json();
          if (data.user) {
            setUser(normalizeUser(data.user));
          } else {
            localStorage.removeItem('izwi_token');
            localStorage.removeItem('izwi_session');
          }
        } catch {
          localStorage.removeItem('izwi_token');
          localStorage.removeItem('izwi_session');
        }
      }
      setLoading(false);
    };
    checkSession();
  }, []);

  const signIn = async (email: string, password: string) => {
    const res = await fetch(`${API_BASE}/sign-in`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Sign in failed');
    }
    const data = await res.json();
    localStorage.setItem('izwi_token', data.session?.token);
    localStorage.setItem('izwi_session', JSON.stringify(data));
    setUser(normalizeUser(data.user));
  };

  const signUp = async (name: string, email: string, password: string) => {
    const res = await fetch(`${API_BASE}/sign-up`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Sign up failed');
    }
    const data = await res.json();
    localStorage.setItem('izwi_token', data.session?.token);
    localStorage.setItem('izwi_session', JSON.stringify(data));
    setUser(normalizeUser(data.user));
  };

  const signOut = async () => {
    const token = getStoredToken();
    if (token) {
      await fetch(`${API_BASE}/sign-out`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    localStorage.removeItem('izwi_token');
    localStorage.removeItem('izwi_session');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
